import React from 'react';
import { Link } from 'react-router-dom';
import MarketingLayout from './MarketingLayout';

const FINDINGS = [
  { icon: '💸', stat: '$84B', label: 'Lost to ad fraud globally in 2025', color: '#dc2626', bg: '#fef2f2' },
  { icon: '🤖', stat: '22%', label: 'Of all Google Ads clicks are invalid', color: '#d97706', bg: '#fff7ed' },
  { icon: '🎯', stat: '47%', label: 'Of fraudulent clicks come from competitor IPs', color: '#7c3aed', bg: '#f5f3ff' },
  { icon: '📱', stat: '31%', label: 'Of bot traffic arrives on mobile devices', color: '#059669', bg: '#f0fdf4' },
];

const REPORTS = [
  {
    title: 'The State of Click Fraud in Google Ads — 2026 Report',
    date: 'March 2026', pages: '24 pages', tag: 'Annual Report', tagColor: '#eff6ff', tagText: '#2563eb',
    desc: 'Our most comprehensive analysis of click fraud trends. Covers industry breakdown, geographic hot spots, bot behaviour patterns, and year-over-year trends from 2023–2026.',
    highlights: ['22% average invalid click rate across all verticals', 'Legal & Financial sectors hit hardest at 31%', 'VPN and residential proxies increased 42% YoY'],
  },
  {
    title: 'Competitor Click Fraud: How Common Is It Really?',
    date: 'February 2026', pages: '12 pages', tag: 'Deep Dive', tagColor: '#f5f3ff', tagText: '#7c3aed',
    desc: 'An investigation into organised competitor click fraud. We analysed patterns across 500+ accounts and identified distinct signatures of deliberate competitor targeting versus bot traffic.',
    highlights: ['63% of analysed accounts showed competitor click patterns', 'Attacks cluster between 8–11am and 1–3pm local time', 'SaaS and agency sectors are primary targets'],
  },
  {
    title: 'The ROI of Click Fraud Protection: A Data Study',
    date: 'January 2026', pages: '16 pages', tag: 'Case Study', tagColor: '#f0fdf4', tagText: '#059669',
    desc: 'We measured the actual financial impact of deploying click fraud protection across 200 customer accounts over 90 days. Includes methodology, controls, and statistical confidence intervals.',
    highlights: ['Average 19% improvement in cost per conversion', 'Median payback period: 6.2 days', '94% of accounts showed measurable improvement'],
  },
  {
    title: 'Click Fraud Detection: Signal Analysis Report',
    date: 'December 2025', pages: '20 pages', tag: 'Technical', tagColor: '#fff7ed', tagText: '#d97706',
    desc: 'A technical breakdown of the 40+ signals used in modern click fraud detection. Covers effectiveness ratings, false positive rates, and how signals are weighted in ensemble detection models.',
    highlights: ['Click velocity is the strongest single signal', 'Combining 5+ signals reduces false positives by 87%', 'Device fingerprinting adds 12% detection accuracy'],
  },
];

const STATS = [
  { value: '2.1M+', label: 'Clicks analysed for this research' },
  { value: '200+', label: 'Accounts in our study cohort' },
  { value: '12', label: 'Industries represented in data' },
  { value: '90 days', label: 'Average data collection window' },
];

export default function Research() {
  return (
    <MarketingLayout>
      {/* Hero */}
      <section style={{ padding: '80px 24px 60px', background: 'linear-gradient(180deg,#f8faff 0%,#fff 100%)', textAlign: 'center' }}>
        <div style={{ display: 'inline-block', background: '#ecfeff', color: '#0891b2', fontSize: 12, fontWeight: 700, letterSpacing: '1.2px', textTransform: 'uppercase', padding: '5px 14px', borderRadius: 40, border: '1px solid #a5f3fc', marginBottom: 20 }}>Research</div>
        <h1 style={{ fontSize: 48, fontWeight: 900, letterSpacing: '-2px', marginBottom: 16 }}>Ad Fraud Research & Data</h1>
        <p style={{ fontSize: 18, color: '#475569', maxWidth: 520, margin: '0 auto' }}>Original research and data studies on click fraud, invalid traffic, and the real cost of ad fraud in 2026.</p>
      </section>

      {/* Key findings */}
      <section style={{ padding: '20px 24px 60px', background: '#fff' }}>
        <div style={{ maxWidth: 1000, margin: '0 auto' }}>
          <h2 style={{ fontSize: 22, fontWeight: 800, textAlign: 'center', marginBottom: 32, letterSpacing: '-0.5px' }}>Key findings from our 2026 research</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', gap: 16 }}>
            {FINDINGS.map((f, i) => (
              <div key={i} style={{ background: f.bg, border: `1.5px solid ${f.color}22`, borderRadius: 16, padding: '24px 20px', textAlign: 'center' }}>
                <div style={{ fontSize: 28, marginBottom: 10 }}>{f.icon}</div>
                <div style={{ fontSize: 36, fontWeight: 900, color: f.color, letterSpacing: '-2px', marginBottom: 6 }}>{f.stat}</div>
                <div style={{ fontSize: 13, color: '#475569', lineHeight: 1.5 }}>{f.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Methodology stats */}
      <section style={{ padding: '0 24px 60px', background: '#f8fafc' }}>
        <div style={{ maxWidth: 900, margin: '0 auto', display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 1, background: '#e2e8f0', border: '1px solid #e2e8f0', borderRadius: 16, overflow: 'hidden' }}>
          {STATS.map((s, i) => (
            <div key={i} style={{ background: '#fff', padding: '28px 20px', textAlign: 'center' }}>
              <div style={{ fontSize: 26, fontWeight: 900, color: '#2563eb', letterSpacing: '-1px', marginBottom: 4 }}>{s.value}</div>
              <div style={{ fontSize: 12, color: '#64748b', lineHeight: 1.4 }}>{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Reports */}
      <section style={{ padding: '40px 24px 100px', background: '#fff' }}>
        <div style={{ maxWidth: 860, margin: '0 auto' }}>
          <h2 style={{ fontSize: 26, fontWeight: 800, letterSpacing: '-0.5px', marginBottom: 32 }}>Research reports</h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
            {REPORTS.map((r, i) => (
              <div key={i} style={{ border: '1.5px solid #e2e8f0', borderRadius: 16, padding: '28px 32px', background: '#fff' }}>
                <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 14, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 11, fontWeight: 700, background: r.tagColor, color: r.tagText, padding: '3px 10px', borderRadius: 20 }}>{r.tag}</span>
                  <span style={{ fontSize: 12, color: '#94a3b8' }}>{r.date}</span>
                  <span style={{ fontSize: 12, color: '#94a3b8' }}>·</span>
                  <span style={{ fontSize: 12, color: '#94a3b8' }}>{r.pages}</span>
                </div>
                <h3 style={{ fontSize: 18, fontWeight: 700, color: '#0f172a', lineHeight: 1.4, marginBottom: 10 }}>{r.title}</h3>
                <p style={{ fontSize: 14, color: '#475569', lineHeight: 1.7, marginBottom: 16 }}>{r.desc}</p>
                <div style={{ marginBottom: 20 }}>
                  {r.highlights.map((h, j) => (
                    <div key={j} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 6 }}>
                      <span style={{ color: '#22c55e', fontWeight: 700, fontSize: 14, marginTop: 1, flexShrink: 0 }}>✓</span>
                      <span style={{ fontSize: 13, color: '#334155', lineHeight: 1.5 }}>{h}</span>
                    </div>
                  ))}
                </div>
                <a href="#" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: '#eff6ff', color: '#2563eb', textDecoration: 'none', padding: '9px 18px', borderRadius: 8, fontSize: 13, fontWeight: 600 }}>
                  Download Free Report ↓
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section style={{ padding: '60px 24px 80px', background: '#f8fafc', textAlign: 'center' }}>
        <h2 style={{ fontSize: 28, fontWeight: 800, letterSpacing: '-0.5px', marginBottom: 12 }}>See the data in action</h2>
        <p style={{ fontSize: 16, color: '#64748b', marginBottom: 28 }}>Start your 7-day free trial and discover exactly how much fraud is hitting your campaigns.</p>
        <Link to="/register" style={{ background: '#2563eb', color: '#fff', textDecoration: 'none', padding: '14px 36px', borderRadius: 12, fontSize: 16, fontWeight: 700 }}>Start Free Trial →</Link>
      </section>
    </MarketingLayout>
  );
}
